<template>
    <div>
        <!-- PHOTO UPLOAD -->
        <h2 class="mb-5">
            Bilder hochladen
            <font-awesome-icon
                v-b-popover.hover.right="$t('intern_inserieren.info_image')"
                icon="question-circle"
                class="mm-link"
                style="font-size: 11pt;"
            />
        </h2>
        <div v-if="isMobile" class="col-12 col-sm-3 mt-3 upload-photo">
            <input type="file" @change="addImage" accept="image/*" id="file" multiple>
            <div class="text-center rounded border p-3">Hochladen</div>
        </div>
        <div v-else class="col-4 col-sm-3 mt-3 upload-photo">
            <input type="file" @change="addImage" accept="image/*" id="file" multiple>
            <div class="text-center rounded border p-3">Hochladen</div>
        </div>
        <!-- DRAG TO REORDER LIST OF PHOTOS -->
        <draggable v-if="isMobile" v-model="images" ghost-class="ghost">
            <transition-group type="transition" name="flip-list" class="row">
                <div
                    v-for="(image, index) in images"
                    class="col-12 col-sm-3 mt-3 img current"
                    :class="{'main-photo': index == 0}"
                    :key="image.id"
                    :id="image.id"
                    @click="displayImg(image)"
                >
                    <img :src="getUrl(image)" alt="" class="w-100 h-200">
                    <div class="btn-group position-absolute control-btn" style="right: 10px; z-index: 100;">
                        <button class="btn btn-secondary" @click.stop="openCropModal(image)" v-b-tooltip.hover title="Bild zuschneiden">
                            <font-awesome-icon icon="crop"/>
                        </button>
                        <button class="btn btn-danger" @click.stop="deletePhoto(image, $event)" v-b-tooltip.hover title="Bild löschen">
                            <font-awesome-icon icon="trash-alt"/>
                        </button>
                    </div>
                    <div class="px-5" style="margin-top: -40px;">
                        <b-progress v-if="String(image.id).includes('NEW_IMAGE_UPLOAD')" :value="100" :max="100" animated variant="success"></b-progress>
                    </div>
                </div>
            </transition-group>
        </draggable>
        <draggable v-else v-model="images" ghost-class="ghost">
            <transition-group type="transition" name="flip-list" class="row">
                <div
                    v-for="(image, index) in images"
                    class="col-4 col-sm-3 mt-3 img"
                    :class="{'main-photo': index == 0}"
                    :key="image.id"
                    :id="image.id"
                    @click="displayImg(image)"
                    @mouseenter="showControl($event)"
                    @mouseleave="hideControl($event)"
                >
                    <img :src="getUrl(image)" alt="" class="w-100 h-200">
                    <div class="btn-group position-absolute control-btn" style="right: 10px; z-index: 100;">
                        <!--<button class="btn btn-secondary" @click.stop="openCropModal(image)" v-b-tooltip.hover title="Bild zuschneiden">
                            <font-awesome-icon icon="crop"/>
                        </button>-->
                        <button class="btn btn-danger" @click.stop="deletePhoto(image, $event)" v-b-tooltip.hover title="Bild löschen">
                            <font-awesome-icon icon="trash-alt"/>
                        </button>
                    </div>
                    <div class="px-5" style="margin-top: -40px;">
                        <b-progress v-if="String(image.id).includes('NEW_IMAGE_UPLOAD')" :value="100" :max="100" animated variant="success"></b-progress>
                    </div>
                </div>
            </transition-group>
        </draggable>
        <b-modal ref="display-modal" :title="'Bildvorschau'" size="xl">
            <img :src="displayedImg" alt="Bild" class="w-100">
            <template slot="modal-footer" slot-scope="{ cancel }">
                <b-button variant="secondary" @click="cancel()">Schließen</b-button>
            </template>
        </b-modal>
        <!-- CROP PHOTO BEFORE UPLOAD -->
        <b-modal ref="crop-modal" :title="'Bild zuschneiden'" size="xl" no-close-on-backdrop @shown="updateCropModal">
            <vue-cropper
                ref="cropper"
                :src="imgSrc"
                alt="Bild"
                :aspectRatio="16/9"
                :autoCrop="true"
                :ready="initCropper"
                :restore="false"
                >
            </vue-cropper>
            <template slot="modal-footer" slot-scope="{ cancel }">
                <b-button variant="secondary" @click="cancel()">Schließen</b-button>
                <b-button variant="success" @click="crop()">Hochladen</b-button>
            </template>
        </b-modal>
        <b-modal ref="error-modal" :title="'Fehlermeldung'">
            <div class="d-block">
                <h5>{{$t('intern_object_erstellen.photo_error')}}</h5>
            </div>
            <template slot="modal-footer" slot-scope="{ cancel }">
                <b-button variant="danger" @click="cancel()">Schließen</b-button>
            </template>
        </b-modal>
        <p class="mt-5"><strong>Tipp: </strong>Wir empfehlen Ihnen Bilder zu verwenden, die mindestens 800x450 Pixel groß sind. Falls Sie Bilder in mehreren Formaten hochladen, wählen Sie als Vorschaubild eines mit dem Format 16:9.</p>
        <!-- VIDEO UPLOAD  -->
        <h2 class="mt-5 mb-3">
            Video hochladen
            <font-awesome-icon
                v-b-popover.hover.right="$t('intern_inserieren.info_video')"
                icon="question-circle"
                class="mm-link"
                style="font-size: 11pt;"
            />
        </h2>
        <div class="input-group mb-3">
            <div class="custom-file">
                <input type="file" ref="video" class="custom-file-input" @change="addVideo" accept="video/mp4">
                <label v-if="isMobile" class="custom-file-label">{{video_label_Mobile}}</label>
                <label v-else class="custom-file-label">{{video_label}}</label>
            </div>
        </div>
        <!-- DISPLAY VIDEO -->
        <video controls width="250" v-if="video_url">
            <source :src="video_url" type="video/mp4">
        </video>
        <p class="mt-3">
            Alternativ können Sie ein YouTube-Video einbetten
            <font-awesome-icon
                v-b-popover.hover.right="$t('intern_inserieren.info_video_youtube')"
                icon="question-circle"
                class="mm-link"
                style="font-size: 11pt;"
            />
        </p>
        <textarea v-model.trim="video_embed" class="form-control" placeholder="Fügen Sie den neuen Einbettungscode hier ein"></textarea>
        <!-- DISPLAY VIDEO -->
        <div class="mt-3" v-html="video_embed"></div>
        <div v-if="isMobile" class="mt-3 text-center">
            <button v-if="create"  class="btn btn-success w-100" @click="weiter()" :disabled="save_disabled">Weiter</button>
            <button v-if="!create" class="btn btn-success mb-1 w-100" @click="weiter(false,true)" :disabled="save_disabled">Speichern und Schließen</button>
            <button v-if="!create" class="btn btn-success w-100" @click="weiter()" :disabled="save_disabled">Weiter</button>
        </div>
        <div v-else class="mt-3 text-right">
            <button v-if="create"  class="btn btn-success" @click="weiter()" :disabled="save_disabled">Weiter</button>
            <button v-if="!create" class="btn btn-success mx-2" @click="weiter(false,true)" :disabled="save_disabled">Speichern und Schließen</button>
            <button v-if="!create" class="btn btn-success" @click="weiter()" :disabled="save_disabled">Weiter</button>
        </div>
    </div>
</template>

<script>
    import VueCropper from 'vue-cropperjs';
    import 'cropperjs/dist/cropper.css';
    import Inserat from '../../../classes/Inserat.js';
    import draggable from 'vuedraggable';
    import { IsMobileMixin } from '../../../mixins/IsMobile';

    export default {
        data() {
            return {
                images: [],
                imgSrc: '',
                imgSrcObject: {},
                displayedImg: '',
                video_id: 0,
                video_type: '',
                video_url: '',
                video_already_exist: false,
                video_embed: '',
                video_label: 'Wählen Sie eine Datei',
                video_label_Mobile: 'Datei wählen',
                img_path: '/api/objectApi/getImage?id=',
                form: new FormData(),
                save_disabled: false,
                weiterCalledAlready: false
            }
        },
        props:['create'],
        mixins: [IsMobileMixin],
        watch: {
            video_embed: function(val) {
                if(val){
                    this.video_id             = 0;
                    this.video_url            = '';
                    this.video_label          = 'Wählen Sie eine Datei';
                    this.video_type           = 'youtube';

                    this.$refs['video'].value = '';

                    if(this.create) {
                      let jsonObject = JSON.parse(localStorage.getItem('currentObject'));
                      let inserat = Inserat.getObjectFromJSON(jsonObject);
                      inserat.setVideo(val);
                      inserat.setVideoType('youtube');
                      localStorage.setItem('currentObject', JSON.stringify(inserat));
                    }
                }
            },
            images: function(val) {
                if(val.some(el => String(el.id).includes('NEW_IMAGE_UPLOAD'))){
                    this.save_disabled = true;
                } else {
                    this.save_disabled = false;
                }

                if(this.create){
                    let jsonObject = JSON.parse(localStorage.getItem('currentObject'));
                    let inserat    = Inserat.getObjectFromJSON(jsonObject);
                    inserat.fotos  = this.images.map(el => {
                        return {
                            id: el.id,
                            crop_data: el.crop_data ? el.crop_data : null
                        }
                    });

                    localStorage.setItem('currentObject', JSON.stringify(inserat));
                }

            }
        },
        components: { VueCropper, draggable },
        methods: {
            scrollTop() {
                window.scrollTo(0, 0);
            },
            getUrl(image) {
                if(Object.keys(image).includes('cropped_url')) {
                    return image.cropped_url;
                } else if(Object.keys(image).includes('path_micro')) {
                    return image.path_micro;
                } else if(Object.keys(image).includes('url_path')) {
                    return image.url_path;
                } else if(Object.keys(image).includes('url')) {
                    return image.url;
                }
            },
            showControl(event) {
                event.target.classList.add('current');
            },
            hideControl(event) {
                event.target.classList.remove('current');
            },
            initCropper() {
                if(Object.keys(this.imgSrcObject).includes('crop_data')) {
                    this.$refs.cropper.setData(JSON.parse(JSON.stringify(this.imgSrcObject.crop_data)));
                    this.$refs.cropper.initCrop();
                }
            },
            uploadImage(img, file) {
                let object_id = 0;
                let form = new FormData();

                form.append('file',  file);

                if(this.create){
                    let jsonObject = JSON.parse(localStorage.getItem('currentObject'));
                    let inserat    = Inserat.getObjectFromJSON(jsonObject);
                    object_id      = inserat.getId();
                }else {
                    object_id      = this.$store.state.editObject.id;
                }

                this.$http.post('/objectApi/uploadPhoto?id='+object_id, form, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                }).then( (response) => {
                    this.images = this.images.map(el => {
                        if(el.id == img.id){
                            el.id = response.data;
                        }
                        return el;
                    })
                }).catch(err => {

                });
            },
            addImage(event) {
                this.$store.state.loading = true;
                let num_of_files = event.target.files.length - 1;

                for(let i = 0; i <= num_of_files; i++){
                    const file = event.target.files[i];
                    if (!file.type.includes('image/')) {
                        alert('Please select an image file');
                        return;
                    }

                    if (typeof FileReader === 'function') {
                        const reader  = new FileReader();
                        reader.onload = (event) => {

                            let random_number = new Date();
                            let img_file      = event.target.result;
                            let img_index     = 'image_file_' + this.images.length;
                            let img_object    = {
                                id:  'NEW_IMAGE_UPLOAD' + random_number.getTime(),
                                url:  img_file,
                                img_index: img_index
                            };

                            this.images.push(img_object);

                            // this.form.set(img_index, file);
                            this.uploadImage(img_object, file);

                            if(i == num_of_files){
                                this.$store.state.loading = false;
                            }
                        };
                        reader.readAsDataURL(file);
                    } else {
                        alert('Sorry, FileReader API is not supported');
                    }
                }
            },
            openCropModal(image) {
                this.$store.state.loading = true;
                this.$refs['crop-modal'].show();
                this.imgSrcObject = image;
            },
            updateCropModal(){
                let img_url = '';

                if(Object.keys(this.imgSrcObject).includes('url_path')){
                    img_url = this.imgSrcObject.url_path;
                } else {
                    img_url = this.imgSrcObject.url;
                }
                if(this.imgSrc != img_url){
                    this.imgSrc = img_url;
                    this.$refs['cropper'].replace(this.imgSrc);
                }
                this.$store.state.loading = false;
            },
            crop() {
                this.$store.state.loading = true;

                let crop_data  = this.$refs.cropper.getData();
                let crop_img   = this.$refs.cropper.getCroppedCanvas().toDataURL();

                this.images  = this.images.map((el) => {
                    if(el.id == this.imgSrcObject.id){
                        el.cropped_url = crop_img;
                        el.crop_data   = crop_data;
                    }

                    return el;
                });

                this.$refs['crop-modal'].hide();
                this.$store.state.loading = false;
            },
            displayImg(img) {
                let src = img.url ? img.url : img.url_path;

                this.$refs['display-modal'].show();
                if(Object.keys(img).includes('crop_data')){
                    if(Object.keys(img).includes('cropped_url')) {
                        src = img.cropped_url;
                    } else if(Object.keys(img).includes('preview')){
                        src = img.preview;
                    }
                }

                this.displayedImg = src;
            },
            deletePhoto(photo, e) {
              if(this.images.length > 1){
                e.stopPropagation();
                let check_if_new = photo.id.length >= 3
                    && (photo.id.substr(0,3) == 'NEW'  || photo.id.substr(0,19) == 'uploadedButNotSaved' );

                if(check_if_new){
                    this.images  = this.images.filter((img) => img.id != photo.id);
                    this.form.delete(photo.img_index);
                }else {
                    this.$bvModal.msgBoxConfirm('Möchten Sie das Foto wirklich löschen?',{
                        okVariant:   'danger',
                        okTitle:     'Löschen',
                        cancelTitle: 'Abbrechen'
                    }).then(value => {
                        if(value){
                            this.$store.state.loading = true;

                            this.$http.delete('/objectApi/deleteImage?id=' + photo.id).then( () => {
                                this.images  = this.images.filter((img) => img.id != photo.id);
                                this.$store.state.loading = false;
                            }).catch(error => {
                                this.$store.state.loading = false;
                                this.$emit('error', error.response.data.data);
                            });
                        }
                    }).catch(err => {
                        console.log(err);
                    });
                }
              }
              else
              {
                this.$emit('error', 'Das Bild kann nicht gelöscht werden, da es mindestens ein Bild für die Vorschau geben sollte.');
              }
            },
            addVideo(event) {
                const file  = event.target.files[0];

                if (!file.type.includes('video/mp4')) {
                    alert('Please select a Video file');
                    return;
                }

                if (typeof FileReader === 'function') {
                    const reader   = new FileReader();
                    this.video_url = '';
                    reader.onload  = (event) => {
                        this.video_id      = 0;
                        this.video_url     = event.target.result;
                        this.video_already_exist = false;
                        this.video_type    = 'mietmeile';
                        this.video_embed   = '';

                        let jsonObject = JSON.parse(localStorage.getItem('currentObject'));
                        let inserat = (jsonObject == null) ? new Inserat : Inserat.getObjectFromJSON(jsonObject);
                        inserat.setVideo(this.video_url);
                        inserat.setVideoType('mietmeile');

                        localStorage.setItem('currentObject', JSON.stringify(inserat));
                    }
                    reader.readAsDataURL(file);
                    this.video_label = file.name;
                } else {
                    alert('Sorry, FileReader API not supported');
                }
            },
            weiter(gotoNextTab = true, closeAfter = false) {
                this.weiterCalledAlready = true;
                if(this.images.length != 0){
                    this.$store.state.loading = true;

                    let object_id  = 0;

                    let inserat    = null;
                    const video    = {
                        url:                  this.video_url,
                        video_already_exist:  this.video_already_exist,
                        type:                 this.video_type,
                        embed:                this.video_embed,
                        name:                 this.video_label
                    };

                    if(this.create){
                        let jsonObject = JSON.parse(localStorage.getItem('currentObject'));
                        inserat        = Inserat.getObjectFromJSON(jsonObject);
                        object_id      = inserat.getId();
                    }else {
                        object_id      = this.$store.state.editObject.id;
                    }

                    this.images.forEach((img, index) => {
                        let img_object = {
                            id: img.id
                        };

                        if(Object.keys(img).includes('crop_data')){
                            img_object.crop_x = img.crop_data.x;
                            img_object.crop_y = img.crop_data.y;
                            img_object.crop_w  = img.crop_data.width;
                            img_object.crop_h = img.crop_data.height;
                        }

                        if(Object.keys(img).includes('img_index')){
                            img_object.img_index = img.img_index;
                        }
                        this.form.append('images['+index+']', JSON.stringify(img_object));
                    });

                    if(this.video_type != ''){
                        this.form.append('video', JSON.stringify(video));
                        this.form.append('video_id', JSON.stringify(this.video_id));
                    }

                    this.$http.post('/objectApi/uploadPhotoVideo?id='+object_id, this.form, {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        }
                    }).then( (response) => {
                        this.$store.state.loading = false;
                        const data = JSON.parse(response.data);

                        if(data.msg != 'Erfolgreich gespeichert'){
                            this.$bvToast.toast('Etwas ist schief gegangen, bitte wiederholen', {
                                variant: 'danger',
                                title: 'Fehlermeldung'
                            });
                        } else {
                            if(this.create){
                                inserat.fotos = this.images;
                                inserat.video = data.video_id;
                                inserat.videoType = data.video_type;
                                localStorage.setItem('currentObject', JSON.stringify(inserat));
                            }
                            if(gotoNextTab){
                                localStorage.setItem('currentObjectTab', 'price');
                                this.$emit('nextTab','price');
                            }

                            if(closeAfter){
                              this.$emit('success', 'Erfolgreich gespeichert');
                              setTimeout(() => {
                                this.$router.push({name: 'inserat-uebersicht', params: {page: this.$store.state.lastOpenedPage}});
                              }, 1000);
                            }
                        }

                    }).catch(error => {
                        this.$store.state.loading = false;
                        console.log('error');
                        this.$bvToast.toast('test test test Etwas ist schief gegangen, bitte wiederholen', {
                            variant: 'danger',
                            title: 'Fehlermeldung'
                        });
                    });
                }else {
                    this.$refs['error-modal'].show();
                }
            }
        },
        created() {
            this.scrollTop();
            let id = 0;
            if(this.create){
                let jsonObject = JSON.parse(localStorage.getItem('currentObject'));
                let inserat    = Inserat.getObjectFromJSON(jsonObject);
                let img_ids    = inserat.fotos.reduce((a, b) => a + ',' + b.id, '0');
                id             = inserat.getId();

                if(inserat.video){
                    this.$http.get('/objectApi/getVideo?id='+inserat.video+'&saved=false').then(response => {
                        let video = JSON.parse(response.data);

                        if(video && Object.keys(video).length > 2){
                            this.video_type  = video.video_provider;

                            if(video.video_provider == 'mietmeile'){
                                this.video_id  = video.video_id;
                                this.video_url = video.video_url;
                            }else {
                                this.video_id    = video.video_id;
                                this.video_embed = video.yt_code;
                            }
                        }
                    }).catch(error => {
                        this.$emit('error', error.response.data.data);
                    });
                }

                if(inserat.fotos.length > 0) {
                    this.$http.get('/objectApi/getAllTempImages?ids='+img_ids).then(response => {
                        let images  = JSON.parse(response.data);

                        this.images = images.map(img => {
                            img.url_path = this.img_path + img.id;
                            img.crop_data = {
                                x:      parseInt(img.crop_x),
                                y:      parseInt(img.crop_y),
                                width:  parseInt(img.crop_w),
                                height: parseInt(img.crop_h)
                            };

                            return img;
                        });
                    }).catch(err => {console.log(err);});
                }
            }else {
                let object     = this.$store.state.editObject;
                id             = object.id;
            }

            if(id > 0){
                this.$http.get('/objectApi/getAllImages?id='+id).then(response => {
                    let images  = JSON.parse(response.data);

                    this.images = images.map(img => {
                        img.url_path    = this.img_path + img.id;
                        img.crop_data = {
                            x:      parseInt(img.crop_x),
                            y:      parseInt(img.crop_y),
                            width:  parseInt(img.crop_w),
                            height: parseInt(img.crop_h)
                        };

                        return img;
                    });
                }).catch(error => {
                    this.$emit('error', error.response.data.data);
                });

                this.$http.get('/objectApi/getVideo?id='+id).then(response => {
                    let video = JSON.parse(response.data);

                    if(video && Object.keys(video).length > 2){
                        this.video_type  = video.video_provider;
                        this.video_already_exist = true;

                        if(video.video_provider == 'mietmeile'){
                            this.video_id  = video.video_id;
                            this.video_url = video.video_url;
                        }else {
                            this.video_id    = video.video_id;
                            this.video_embed = video.yt_code;
                        }
                    }
                }).catch(error => {
                    this.$emit('error', error.response.data.data);
                });
            }
        },
        beforeDestroy() {
          if(this.create && !this.weiterCalledAlready){
            this.weiter(false);
          }
        }
    }
</script>

<style scoped>
#file{
    opacity: 0;
    z-index: 10;
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    bottom: 0;
    cursor: pointer;
}
.delete{
    position: absolute;
    top: 0;
    right: 20px;
    cursor: pointer;
    font-weight: 800;
    font-size: 22px;
    color: red;
    text-shadow: 1px 1px 1px #000, -1px 1px 1px #000, 1px -1px 1px #000, -1px -1px 1px #000;
}
/* .sortable-drag{
    opacity: 0;
} */
.flip-list-move{
    transition: transform 0.5s;
}
.ghost{
    border: 5px solid #000;
    background-color: #000;
    opacity: 0.7;
}
.img{
    cursor: pointer;
    padding: 10px;
    background-color: #d7d7d7;
    border: 4px solid white;
    height: 200px;
}
.h-200{
    height: 170px;
}
.custom-file-label::after {
    content: 'Durchsuchen' !important;
}
textarea{
    resize: none;
    height: 150px;
}
.upload-photo:hover div{
    background: #dfdfdf;
}
.main-photo{
    background-color: #5FA963;
}
.current .control-btn{
    position: relative;
    display: -ms-inline-flexbox;
    display: -webkit-inline-box;
    display: inline-flex;
}
.control-btn{
    display: none;
}
</style>
